import { h, Component } from 'preact';
import Store from 'store/store';
import { joinClasses, triggerAnim } from 'utils/utils';
import { getPreparedPreviewData } from 'utils/preview';
import { showLogin, getCurrentUser, hideLogin } from 'utils/login';
import { API_ORIGIN, request } from 'utils/api';
import { FOCUS_STUDIO_SLUG, UNIVERSAL_STUDIO_SLUG } from 'utils/constants';
import { getCurrentHubDomain } from 'utils/studioConfig';
import { getThundrData } from 'utils/thundr';

import Login from 'components/login/login';
import Powster from 'components/icons/powster';
import SimpleField from 'components/editor/settings/simpleField';
import Button from 'components/button/button';

import s from 'base/universalCMSPreview/universalCMSPreviewApp.sss';

const qs = typeof window !== 'undefined' ? new URLSearchParams(location.search) : new URLSearchParams();

// Local-only preview roots. Demo / stage / prod hosts come from the user's
// /me regions[].domain_name (returned by the BE, env-aware), so they don't
// need to be hardcoded here. Adding a new region in future is no FE change.
const STUDIO_LOCAL_PREVIEW_ROOTS = {
	[UNIVERSAL_STUDIO_SLUG]: (region = 'us') => `http://localhost:4010/preview/universalstudios/universal-hub/${region}/local/`,
	[FOCUS_STUDIO_SLUG]: (region = 'gb') => `http://localhost:4010/preview/focusfeatures/focus-features-hub/${region}/local/`,
};

export default class UniversalCMSPreviewApp extends Component {
	constructor(props) {
		super(props);

		this.state = {
			auth: false,
			store: props.initialStore,
		};

		this.submitPassword = this.submitPassword.bind(this);
		this.onMessage = this.onMessage.bind(this);

		Store.set(props.initialStore);
		Store.on('update', (event) => {
			this.setState({ store: Store.get() });

			// Notify preview iframe to invalidate navigation cache when navigation data changes
			const hasNavigationChange = event?.events?.some(e =>
				e.path && e.path.length >= 2 && e.path[0] === 'data' && e.path[1] === 'navigation'
			);
			if (hasNavigationChange) {
				const iframe = this.base?.querySelector('iframe');
				if (iframe?.contentWindow) {
					iframe.contentWindow.postMessage({ type: 'invalidateCache', name: 'navigation' }, '*');
				}
			}
		});
	}

	componentDidMount() {
		window.addEventListener('message', this.onMessage);

		const previewId = qs.get('x');
		if (previewId) {
			this.loadSharedPreview(previewId, qs.get('pwd'));
		} else {
			this.initLoggedInPreview();
		}
	}

	componentWillUnmount() {
		window.removeEventListener('message', this.onMessage);
	}

	getPreviewRoot(studio = UNIVERSAL_STUDIO_SLUG, region) {
		if (qs.has('local')) {
			const cfg = STUDIO_LOCAL_PREVIEW_ROOTS[studio];
			return cfg?.(region);
		}
		// state.previewDomain is set only for shared previews (from /preview/{id}) and
		// is authoritative for the app being shared, including when a logged-in user
		// opens a shared preview for a region/studio that isn't their own. Otherwise
		// fall back to the current user's region domain.
		const domain = this.state.previewDomain || getCurrentHubDomain();
		return domain ? `https://${domain}/` : undefined;
	}

	async loadSharedPreview(id, password) {
		try {
			const qs = {};
			if (password) qs.password = password;
			const res = await request(`/preview/${id}`, { qs });
			const path = res.live_url || '/';
			const previewData = res.content || {};
			this.setState({
				ready: true,
				requirePassword: false,
				error: false,
				iframePath: path,
				forceDemo: res.demo_mode,
				previewStudio: res.studio,
				previewRegion: res.region,
				previewDomain: res.domain_name,
				previewData,
			});
			if (path.startsWith('/movies/') || path.startsWith('/films/')) {
				const slug = path.split('/')[2];
				await this.adjustIframePathForMovies(previewData[slug], path);
			}
		} catch (err) {
			if (err?.code === 401 || err?.code === 403) {
				this.setState({ requirePassword: true });
				const form = password && this.base.querySelector('form');
				if (form) {
					triggerAnim(form, s.hasError);
				}
			} else if (err?.code === 410) {
				this.setState({ error: true, expired: true });
			} else {
				this.setState({ error: true });
			}
		}
	}

	async submitPassword(e) {
		e.preventDefault();
		const password = e.target.elements?.password?.value;
		if (!password) return;
		// TODO: generic function to get the preview id from the url
		await this.loadSharedPreview(qs.get('x'), password);
	}

	async setActiveUser(user) {
		const regions = Object.entries(user.regions || {}).map(([id, region]) => ({ ...region, region_id: id }));
		Store.get().set({ domains: { regions, selectedRegion: user.region_id } }).now();
		await this.loadContent();
		this.setState({ ready: true });
	}

	async loadContent() {
		const path = qs.get('u');
		// TODO: less flimsy check for movie pages, since this path can change based on the region
		if (path.startsWith('/movies/') || path.startsWith('/films/')) {
			const slug = path.split('/')[2];
			const preparedPreviewData = await getPreparedPreviewData(slug);
			await this.adjustIframePathForMovies(preparedPreviewData, path);
		}
	}

	async adjustIframePathForMovies(previewData, originalPath) {
		if (!previewData || !previewData.thundr_id) return;
		// Preserve the per-page sub-path (e.g. /tickets/, /watch-at-home/) when routing
		// through demo-iframe / prod-iframe. Without it, the iframe always loads at the
		// movie landing and SHOWTIMES_SHOW / HOMEENT_SHOW never fire on first load.
		const subPath = (originalPath || '').replace(/^\/(movies|films)\/[^/]+\/?/, '');
		if (qs.has('local')) {
			const thundrData = await getThundrData(previewData);
			const studio = thundrData?.meta?.studio?.slug;
			const title = thundrData?.meta?.title?.slug;
			const region = thundrData?.meta?.region?.slug;
			if (studio && title && region) {
				this.setState({ iframePath: `http://localhost:4010/preview/${studio}/${title}/${region}/local/${subPath}` });
			}
		} else if (qs.has('demo') || this.state.forceDemo || previewData.ready_state === 'demo') {
			this.setState({ iframePath: `${API_ORIGIN}/demo-iframe/${previewData.thundr_id}/${subPath}` });
		} else if (previewData.ready_state === 'prod') {
			this.setState({ iframePath: `${API_ORIGIN}/prod-iframe/${previewData.thundr_id}/${subPath}` });
		}
	}

	async initLoggedInPreview() {
		const user = await getCurrentUser();
		if (!user) {
			await showLogin();
		} else {
			await this.setActiveUser(user);
		}
	}

	async onMessage(e) {
		if (e.data?.type === 'auth-success') {
			hideLogin();
			return this.setActiveUser(e.data.user);
		}

		let message = e.data;
		if (message?.type === 'requestData') {
			const name = message.name;

			// Read all preview data from localStorage (navigation and movie data)
			// preparePreviewData() packages both navigation and movie data with footer merged
			const data = this.state.previewData ? (
				this.state.previewData[name] || Object.values(this.state.previewData).find(d => d?.thundr_id == name)
			) : await getPreparedPreviewData(name);

			this.base.querySelector('iframe')?.contentWindow.postMessage({ type: 'data', name, data: data || null }, '*');
		}
	}

	getStudio() {
		return this.state.previewStudio || qs.get('s') || UNIVERSAL_STUDIO_SLUG;
	}

	getRegion() {
		return this.state.previewRegion || qs.get('r') || STUDIO_LOCAL_PREVIEW_ROOTS[this.getStudio()]?.defaultRegion;
	}

	getIframePath() {
		if (this.state.iframePath) {
			return this.state.iframePath;
		}
		let path = qs.get('u');
		if (!path) return '/';

		// Transform /movies/ to /films/ for Focus Features (fallback if URL generation didn't handle it)
		const studio = this.getStudio();
		if (studio === FOCUS_STUDIO_SLUG && path.startsWith('/movies/')) {
			path = path.replace('/movies/', '/films/');
		}

		// Handle placeholder paths for both Universal (/movies/) and Focus (/films/)
		if (qs.has('placeholder') && studio === UNIVERSAL_STUDIO_SLUG && path.startsWith('/movies/')) {
			// Should we pass the slug? `/movies/movie-placeholder/?placeholder-slug=${path.split('/')[2]}`
			path = '/movies/movie-placeholder/';
		}

		if ((__IS_LOCAL__ || __IS_DEMO__) && studio === FOCUS_STUDIO_SLUG && path.startsWith('/films/')) {
			// on local/demo environments for focus, we use hash-based routing
			path = path.replace(/^([^?]+)(\?.*)?$/, '$2#$1');
		}
		return path;
	}

	getPreviewUrl() {
		const path = this.getIframePath();
		const formattedPath = path.replace(/^\//, './');
		const studio = this.getStudio();
		const region = this.getRegion();
		const previewRoot = this.getPreviewRoot(studio, region) || location.href;
		const url = new URL(formattedPath, previewRoot);
		url.searchParams.set('cms-preview', '1');
		return url.href;
	}

	renderHelp() {
		return (
			<p class={s.help}>
				Already have CMS access? <a href="https://cms.universalpictures.com/" target="_blank">Sign in</a>
			</p>
		);
	}

	renderContent() {
		if (this.state.requirePassword) {
			return (
				<div class={s.requirePassword}>
					<div class={s.header}>
						<Powster class={s.logo} />
						<h2>Password required</h2>
						<p>This page requires a password to access</p>
					</div>
					<form onSubmit={this.submitPassword}>
						{/* <input type="password" name="password" placeholder="type your password here" required /> */}
						<SimpleField type="password" name="password" label="Password" required />
						<Button type="submit" primary>Continue</Button>
					</form>
					{/* {this.renderHelp()} */}
				</div>
			);
		}
		if (this.state.error) {
			let message = this.state.expired ? 'This link has expired' : 'This link is not available';
			return (
				<div class={s.error} key="error">
					<div class={s.header}>
						<Powster class={s.logo} />
						<h2>{message}</h2>
					</div>
				</div>
			);
		}
		if (!this.state.ready) {
			return <div class={s.loading} key="loading" />;
		}
		return <iframe src={this.getPreviewUrl()} class={joinClasses(qs.has('mobile') && s.mobile)} />;
	}

	render() {
		return (
			<div class={joinClasses(s.container)} id="universalCMSPreview">
				<Login />
				{this.renderContent()}
			</div>
		);
	}
}
